import { useEffect, useState, useCallback } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import api from "@/lib/api";
import { CATEGORIAS } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, FileDown, FileText, BarChart3, Filter } from "lucide-react";
import { toast } from "sonner";

export default function Relatorios() {
  const [reports, setReports] = useState({});
  const [tipo, setTipo] = useState("posicao_estoque");
  const [units, setUnits] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [f, setF] = useState({ data_ini: "", data_fim: "", unit_id: "TODAS", categoria: "TODAS", sku_id: "TODOS" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/relatorios").then((r) => setReports(r.data));
    api.get("/units").then((r) => setUnits(r.data));
    api.get("/produtos").then((r) => setProdutos(r.data));
  }, []);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (f.data_ini) params.data_ini = f.data_ini;
      if (f.data_fim) params.data_fim = f.data_fim;
      if (f.unit_id !== "TODAS") params.unit_id = f.unit_id;
      if (f.categoria !== "TODAS") params.categoria = f.categoria;
      if (f.sku_id !== "TODOS") params.sku_id = f.sku_id;
      const { data } = await api.get(`/relatorios/${tipo}`, { params });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar relatório"); }
    finally { setLoading(false); }
  }, [tipo, f]);

  useEffect(() => { run(); }, [tipo]); // eslint-disable-line

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const exportCSV = () => {
    if (!data?.linhas.length) return toast.error("Sem dados para exportar");
    const head = data.colunas.map((c) => `"${c.label}"`).join(";");
    const body = data.linhas.map((r) => data.colunas.map((c) => `"${String(r[c.key] ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + head + "\n" + body], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `nexolog_${tipo}.csv`;
    a.click();
    toast.success("CSV exportado");
  };

  const exportPDF = () => {
    if (!data?.linhas.length) return toast.error("Sem dados para exportar");
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(16);
    doc.text(`NEXO LOG — ${data.titulo}`, 14, 16);
    doc.setFontSize(9);
    doc.text(`Uni Alimentos · gerado em ${new Date().toLocaleString("pt-BR")}`, 14, 22);
    autoTable(doc, {
      startY: 28,
      head: [data.colunas.map((c) => c.label)],
      body: data.linhas.map((r) => data.colunas.map((c) => String(r[c.key] ?? ""))),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [245, 158, 11], textColor: [20, 20, 20] },
    });
    doc.save(`nexolog_${tipo}.pdf`);
    toast.success("PDF exportado");
  };

  return (
    <div className="space-y-5" data-testid="relatorios-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Gestão</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><BarChart3 className="w-8 h-8 text-primary" /> Relatórios</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCSV} data-testid="btn-export-csv"><FileDown className="w-4 h-4" /> CSV</Button>
          <Button onClick={exportPDF} data-testid="btn-export-pdf"><FileText className="w-4 h-4" /> PDF</Button>
        </div>
      </div>

      <Card className="bg-card/70 p-4 space-y-4">
        <div className="flex flex-nowrap sm:flex-wrap gap-2 overflow-x-auto pb-1 sm:overflow-visible">
          {Object.entries(reports).map(([id, label]) => (
            <Button key={id} variant={tipo === id ? "default" : "outline"} size="sm" className="shrink-0" onClick={() => setTipo(id)} data-testid={`report-${id}`}>{label}</Button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div><Label className="text-xs">Data Início</Label><Input type="date" value={f.data_ini} onChange={(e) => set("data_ini", e.target.value)} data-testid="rel-data-ini" /></div>
          <div><Label className="text-xs">Data Fim</Label><Input type="date" value={f.data_fim} onChange={(e) => set("data_fim", e.target.value)} data-testid="rel-data-fim" /></div>
          <div>
            <Label className="text-xs">Unidade</Label>
            <Select value={f.unit_id} onValueChange={(v) => set("unit_id", v)}>
              <SelectTrigger data-testid="rel-unidade"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="TODAS">Todas</SelectItem>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.codigo}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Categoria</Label>
            <Select value={f.categoria} onValueChange={(v) => set("categoria", v)}>
              <SelectTrigger data-testid="rel-categoria"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="TODAS">Todas</SelectItem>{CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Produto</Label>
            <Select value={f.sku_id} onValueChange={(v) => set("sku_id", v)}>
              <SelectTrigger data-testid="rel-produto"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="TODOS">Todos</SelectItem>{produtos.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={run} data-testid="btn-gerar-relatorio"><Filter className="w-4 h-4" /> Aplicar Filtros</Button>
      </Card>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : data ? (
        <>
          {data.resumo?.Aviso && (
            <Card className="bg-amber-500/10 border-amber-500/50 p-4 text-amber-200 flex items-center gap-3" data-testid="report-aviso">
              <BarChart3 className="w-5 h-5 shrink-0" /> <span className="text-sm font-medium">{data.resumo.Aviso}</span>
            </Card>
          )}
          {data.resumo && Object.keys(data.resumo).filter((k) => k !== "Aviso").length > 0 && (
            <div className="flex flex-wrap gap-3">
              {Object.entries(data.resumo).filter(([k]) => k !== "Aviso").map(([k, v]) => (
                <Card key={k} className="bg-card/70 px-4 py-3" data-testid={`resumo-${k}`}>
                  <div className="text-xs text-muted-foreground uppercase">{k}</div>
                  <div className="font-display text-2xl font-bold text-primary">{v}</div>
                </Card>
              ))}
            </div>
          )}
          <Card className="bg-card/70 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>{data.colunas.map((c) => <th key={c.key} className="text-left p-3 whitespace-nowrap">{c.label}</th>)}</tr>
              </thead>
              <tbody data-testid="relatorio-tabela">
                {data.linhas.map((r, i) => (
                  <tr key={i} className="border-b border-border/60 hover:bg-secondary/30">
                    {data.colunas.map((c) => <td key={c.key} className="p-3 whitespace-nowrap font-mono text-[13px]">{String(r[c.key] ?? "")}</td>)}
                  </tr>
                ))}
                {!data.linhas.length && <tr><td colSpan={data.colunas.length} className="p-10 text-center text-muted-foreground">Sem dados para os filtros selecionados.</td></tr>}
              </tbody>
            </table>
          </Card>
          <div className="text-xs text-muted-foreground">{data.linhas.length} registro(s)</div>
        </>
      ) : null}
    </div>
  );
}
